import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import DualRangeSlider from '../Aldersprofil/DualRangeSlider'
import { fetchMerkeOptions, fetchForstegangsaar } from './api'
import type { MerkeRow } from './api'

interface Filters {
  from: string
  to: string
  klasse: string[]
  varebil: boolean | undefined
  omregistrering: boolean
  // Mine markedsområder (#489) — the merke typeahead and the år-slider bounds
  // must describe the same market the tab below them does.
  selgerPostnummer?: string | null
  kjoperPostnummer?: string | null
}

interface Props {
  filters: Filters
  merke: string[]
  aarMin: number | undefined
  aarMax: number | undefined
  onChange: (partial: {
    merke?: string[]
    aarMin?: number | undefined
    aarMax?: number | undefined
  }) => void
}

const DEBOUNCE_MS = 250
// Cars first registered before 2000 are not relevant for this comparison —
// the år-range slider never goes below this floor.
const AAR_MIN_FLOOR = 2000

/**
 * Sammenligning-tab filter bar: a searchable Merke multiselect and a
 * første-gangs-år range slider. The klasse dimension comes from the global
 * Klasse filter above the page, so it is not repeated here.
 *
 * Selections are *staged* in local draft state — changing the merke list or
 * dragging the år-slider does NOT fetch. The query only runs when the user
 * clicks "Vis resultater". This keeps the slider (a native range input that
 * fires onChange on every step) from spamming the backend with one slow query
 * per pixel of drag. `onChange` mirrors the `setFilters` partial shape so the
 * page forwards it straight to useBruktbilFilters; "Nullstill" applies a reset
 * immediately.
 */
export default function FilterBar({
  filters,
  merke,
  aarMin,
  aarMax,
  onChange,
}: Props) {
  const { t } = useTranslation('bruktbil')
  // Draft (staged) selection. Re-synced from props whenever the applied filters
  // change from the outside — e.g. an Oversikt row-click that navigates here
  // with a merke already set.
  const [draftMerke, setDraftMerke] = useState<string[]>(merke)
  const [draftAarMin, setDraftAarMin] = useState<number | undefined>(aarMin)
  const [draftAarMax, setDraftAarMax] = useState<number | undefined>(aarMax)

  const [merkeQuery, setMerkeQuery] = useState('')
  const [merkeOptions, setMerkeOptions] = useState<MerkeRow[]>([])
  const [merkeOpen, setMerkeOpen] = useState(false)
  const [bounds, setBounds] = useState<[number, number] | null>(null)

  const klasseKey = JSON.stringify(filters.klasse)
  const appliedMerkeKey = JSON.stringify(merke)

  // Keep the draft in sync with externally-applied filters.
  useEffect(() => {
    setDraftMerke(merke)
    setDraftAarMin(aarMin)
    setDraftAarMax(aarMax)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedMerkeKey, aarMin, aarMax])

  // Merke typeahead — empty query fetches the full list (click-to-open
  // dropdown). Driven by the draft år-range so the options preview the
  // pending selection.
  useLatest(alive => {
    const timer = setTimeout(() => {
      fetchMerkeOptions(filters, merkeQuery, draftAarMin, draftAarMax)
        .then(r => { if (alive()) setMerkeOptions(r.data.rows) })
        .catch(() => { if (alive()) setMerkeOptions([]) })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [filters.from, filters.to, klasseKey, filters.varebil, filters.selgerPostnummer, filters.kjoperPostnummer, merkeQuery, draftAarMin, draftAarMax])

  // År-range slider bounds. The lower bound is floored at AAR_MIN_FLOOR so
  // the user can never select pre-2000 first-registration years.
  useLatest(alive => {
    fetchForstegangsaar(filters)
      .then(r => {
        if (!alive()) return
        const rows = r.data.rows
        if (rows.length > 0) {
          const dataMin = rows[0].year
          const dataMax = rows[rows.length - 1].year
          setBounds([Math.max(AAR_MIN_FLOOR, dataMin), dataMax])
        }
      })
      .catch(() => { if (alive()) setBounds(null) })
  }, [filters.from, filters.to, klasseKey, filters.varebil, filters.selgerPostnummer, filters.kjoperPostnummer])

  function addMerke(name: string) {
    if (draftMerke.includes(name)) return
    setDraftMerke([...draftMerke, name])
    setMerkeQuery('')
  }

  function removeMerke(name: string) {
    setDraftMerke(draftMerke.filter(m => m !== name))
  }

  function handleRange(next: [number, number]) {
    if (!bounds) return
    setDraftAarMin(next[0] === bounds[0] ? undefined : next[0])
    setDraftAarMax(next[1] === bounds[1] ? undefined : next[1])
  }

  function apply() {
    onChange({ merke: draftMerke, aarMin: draftAarMin, aarMax: draftAarMax })
  }

  function nullstill() {
    setDraftMerke([])
    setDraftAarMin(undefined)
    setDraftAarMax(undefined)
    setMerkeQuery('')
    onChange({ merke: [], aarMin: undefined, aarMax: undefined })
  }

  const isDirty =
    JSON.stringify(draftMerke) !== appliedMerkeKey ||
    draftAarMin !== aarMin ||
    draftAarMax !== aarMax

  return (
    <div className="card p-3 flex flex-wrap gap-3 items-end">
      {/* Merke multiselect */}
      <div className="relative min-w-[220px]">
        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('common.merke')}</label>
        <div className="flex flex-wrap gap-1 mb-1">
          {draftMerke.map(m => (
            <span
              key={m}
              className="inline-flex items-center gap-1 rounded-sm bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs px-2 py-0.5"
            >
              {m}
              <button
                type="button"
                data-testid={`remove-merke-${m}`}
                onClick={() => removeMerke(m)}
                className="text-primary-500 hover:text-primary-700"
                aria-label={t('merkeMultiSelect.fjern', { name: m })}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <input
          type="text"
          data-testid="merke-search"
          value={merkeQuery}
          onFocus={() => setMerkeOpen(true)}
          onChange={e => {
            setMerkeQuery(e.target.value)
            setMerkeOpen(true)
          }}
          onBlur={() => setTimeout(() => setMerkeOpen(false), 150)}
          placeholder={t('merkeModell.filterBar.velgMerke')}
          className="w-full text-sm rounded-sm border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-2 py-1"
        />
        {merkeOpen && merkeOptions.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-sm">
            {merkeOptions.map(o => (
              <li key={o.merke ?? ''}>
                <button
                  type="button"
                  // Keep input focus so the dropdown stays open for multi-pick.
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => o.merke && addMerke(o.merke)}
                  className="block w-full text-left text-sm px-2 py-1 hover:bg-gray-50 dark:hover:bg-dark-border"
                >
                  {o.merke ?? '—'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Første-gangs-år range */}
      <div className="min-w-[220px]">
        {bounds && bounds[0] < bounds[1] ? (
          <DualRangeSlider
            min={bounds[0]}
            max={bounds[1]}
            value={[draftAarMin ?? bounds[0], draftAarMax ?? bounds[1]]}
            onChange={handleRange}
            label={t('merkeModell.filterBar.forsteGangsAar')}
          />
        ) : (
          <div className="text-xs text-gray-400">{t('merkeModell.filterBar.forsteGangsAar')}</div>
        )}
      </div>

      <button
        type="button"
        data-testid="apply-filters"
        onClick={apply}
        disabled={!isDirty}
        className="text-sm rounded-sm bg-primary-600 text-white px-3 py-1 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {t('merkeModell.filterBar.visResultater')}
      </button>

      <button
        type="button"
        onClick={nullstill}
        className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
      >
        {t('layout.nullstill')}
      </button>
    </div>
  )
}
